import {
  Button,
  Headline,
  LinkInline,
  NotificationBanner,
  Text,
} from '@velocity/ui';
import axios from 'axios';
import ls from 'localstorage-slim';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteProps, useNavigate } from 'react-router-dom';
import { saveUserOffer } from '../../../../api/instant-lease-api';
import Layout from '../../../../layout';
import { setNotification } from '../../../../redux/cache-slice';
import { RootState } from '../../../../redux/types';
import { Offer, Partner, Type } from '../../../../types/instant-lease-api';
import { getText } from '../../../../utils/getter';
import Navigation from '../../../../utils/navigation';
import CarSelection from '../../../b2b/components/car-selection/car-selection';
import ButtonGroup from '../../../b2b/molecules/button-group/button-group';
import ResetPassword from '../../auth-pages/reset-password/reset-password';
import Header from '../../header/header';
import HeaderBar from '../../header/header-bar';
import CouplePhoto from '../../images/overview-photo.jpg';
import SubHeaderBand from '../../sub-header/sub-header-band';
import './overview.css';

// Figma: Desktop 1964:2058 - page content column and text colours
const contentColumn =
  'mx-auto w-full px-4 pt-8 pb-16 md:px-8 md:pt-10 lg:max-w-[784px] lg:px-0 lg:pt-12 lg:pb-[85px]';
const headlineColor = 'text-(--vel-color-secondary-400,#0f3549)!';

const ActiveAppsOverview: React.FC<RouteProps> = () => {
  // Store
  const { leaseApplications } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // Access Token
  const { emailVerified, authId, email, accessToken } = useSelector(
    (state: RootState) => state.auth,
  );

  // Cache State
  const { hasNewOffer } = useSelector((state: RootState) => state.cache);

  // Dispatch hook
  const dispatch = useDispatch();

  // Navigation Hook
  const navigate = useNavigate();

  // Keep only the first app for each publication/pricing pair.
  const uniqueLeaseApplications = leaseApplications.filter(
    (application, index, allApplications) => {
      const publicationId = application.offer?.publication?.publication_id;
      const pricingId = application.offer?.publication?.pricing_id;

      // Only deduplicate when both IDs exist.
      if (publicationId == null || pricingId == null) {
        return true;
      }

      // findIndex returns the first matching pair, so later duplicates are removed.
      return (
        index ===
        allApplications.findIndex(
          (currentApplication) =>
            currentApplication.offer?.publication?.publication_id ===
              publicationId &&
            currentApplication.offer?.publication?.pricing_id === pricingId,
        )
      );
    },
  );

  // User hasMultipleActiveApps
  const hasMultipleActiveApps = leaseApplications.length > 1;

  // Offer data from localstorage if exist
  const [offer, setOffer] = useState<Offer | null>(ls.get<Offer>('oid'));

  // Mobility portal ("Neues Fahrzeug hinzufügen")
  const openMobilityPortal = () =>
    window.open(import.meta.env.VITE_MOPO_URL, '_self');

  // Make sure offer always is up to date (inSync)
  useEffect(() => {
    if (ls.get<Offer>('oid') !== null) {
      setOffer(ls.get<Offer>('oid'));
    }
  }, [hasNewOffer]);

  useEffect(() => {
    // The offerhub URL can send offerId/offerRefId as the literal strings
    // "null" or "undefined" when no real value exists. Both are truthy in
    // JS so we must explicitly filter them out.
    // Either offerId or offerRefId will have a real value — never both.
    const effectiveOfferId =
      offer?.offerId &&
      offer.offerId !== 'null' &&
      offer.offerId !== 'undefined'
        ? offer.offerId
        : undefined;
    const effectiveOfferRefId =
      offer?.offerRefId &&
      offer.offerRefId !== 'null' &&
      offer.offerRefId !== 'undefined'
        ? offer.offerRefId
        : undefined;
    const hasValidOffer = effectiveOfferId || effectiveOfferRefId;

    if (offer && email && hasValidOffer) {
      saveUserOffer({
        accessToken: accessToken,
        offerId: effectiveOfferId,
        email: email,
        pricingId: offer?.pricingId,
        publicationId: offer?.publicationId,
        vin: offer?.vin,
        type: offer?.type,
        partner: offer?.partner as any,
        loyaltyId: offer?.loyaltyId,
        loyaltyType: offer?.loyaltyType,
        offerRefId: effectiveOfferRefId,
      })
        .then(() => {
          ls.remove('oid');
        })
        .catch(() => console.log('error'));
    }
  }, [offer, accessToken, email]);

  // Redirect straight to the application when the user has exactly one.
  // Must run in an effect: openOverview dispatches to the store, which would
  // otherwise update other components (LeaseAppConditions) during render.
  const singleApplicationId =
    leaseApplications.length === 1 ? leaseApplications[0].uuid : undefined;

  useEffect(() => {
    if (emailVerified && singleApplicationId) {
      const { openOverview } = new Navigation();
      navigate(openOverview(singleApplicationId));
    }
  }, [emailVerified, singleApplicationId, navigate]);

  // Resend verification email
  const resendVerificationEmail = () => {
    if (offer) {
      const { offerId, partner, pricingId, publicationId } = offer;
      axios
        .post('/api/notification-v2/send-registration-email', {
          auth0Id: authId,
          partner: partner || Partner.ALD,
          offerId,
          pricingId,
          publicationId,
        })
        .then(() => {
          dispatch(
            setNotification({
              notificationVisibility: true,
              notificationHasError: false,
              notificationTitle: 'E-Mail wurde gesendet',
              notificationBody:
                'Die Mail ist nicht angekommen? Spam-Ordner prüfen oder erneut senden lassen.',
            }),
          );
        })
        .catch(() => {
          dispatch(
            setNotification({
              notificationVisibility: true,
              notificationHasError: true,
              notificationTitle: 'Das tut uns leid.',
              notificationBody: getText('error_unable_to_process_data'),
            }),
          );
        });
    } else {
      dispatch(
        setNotification({
          notificationVisibility: true,
          notificationHasError: true,
          notificationTitle: 'Das tut uns leid.',
          notificationBody: getText('error_unable_to_process_data'),
        }),
      );
    }
  };

  if (!emailVerified) {
    return (
      <>
        <HeaderBar />
        <div className='auth-container verify-email'>
          <div className='auth-form-container login'>
            <div className='auth-form-content login'>
              <h1 className='title'>
                Bitte verifiziere deine <br />
                E-Mail Adresse
              </h1>
              <p className='dark-grey top-16 content-normal'>
                Wir haben dir eine E-Mail mit einem Bestätigungslink an folgende
                E-Mail Adresse geschickt. Bitte klicke in der Mail den Link an,
                um die Registrierung abzuschließen.
              </p>
              <p className='top-24'>
                <span className='content-bold'>E-Mail-Adresse: </span>
                <span className='content-normal'>{email}</span>
              </p>
              <p className='content-bold top-48'>Noch keine E-Mail erhalten?</p>
              <p className='dark-grey top-24 content-normal'>
                Falls du keine E-Mail bekommen hast, überprüfe bitte deinen
                Spam-Ordner. Du kannst auch{' '}
                <a
                  href='/#'
                  onClick={(e) => {
                    e.preventDefault();
                    resendVerificationEmail();
                  }}
                >
                  hier klicken, um die E-Mail erneut anzufordern.
                </a>
              </p>
            </div>
          </div>
          <div className='auth-image-container login'>
            <div
              className='container-background'
              style={{ backgroundImage: `url(${ResetPassword})` }}
            />
          </div>
        </div>
      </>
    );
  }

  if (hasMultipleActiveApps) {
    // Deduplication is applied only to the multi-app list rendering.
    // Newest application first - it is presented as the "Neuer Antrag".
    const sortedApps = _.clone(uniqueLeaseApplications);
    sortedApps.sort((a, b) => {
      return moment(a.offer?.leasing_options?.created).isBefore(
        b.offer?.leasing_options?.created,
      )
        ? 1
        : -1;
    });
    return (
      <>
        <Header showSubHeader={false} />
        <SubHeaderBand
          showPricing={false}
          className='min-h-[92px] md:min-h-[116px] lg:min-h-[120px]'
        />
        <main
          className={contentColumn}
          data-testid='multiple-applications-overview'
        >
          <Headline
            variant='300'
            component='h1'
            className={`m-0! max-w-[582px] ${headlineColor}`}
            data-testid='heading'
          >
            {getText('car_choice')}
          </Headline>
          <Text
            component='p'
            variant='300'
            loose
            className={`m-0! mt-2 ${headlineColor}`}
          >
            {getText('car_selection_intro')}
          </Text>

          <NotificationBanner
            kind='information'
            title={getText('car_selection_info_title')}
            className='mt-6'
            data-testid='multiple-applications-info'
          >
            {getText('car_selection_info_body')}
          </NotificationBanner>

          <div
            className='mt-6 grid grid-cols-1 items-stretch gap-6 md:grid-cols-2'
            data-testid='car-selection-wrapper'
          >
            {sortedApps.map((app, index) => {
              const created = app.offer?.leasing_options?.created;
              const lastChanged = created
                ? moment(created).format('DD.MM.YYYY')
                : '';
              const time = created ? moment(created).format('HH:mm') : '';

              const rate =
                app.type === Type.B2C
                  ? app.offer?.leasing_options?.totals?.rate?.gross
                  : app.offer?.leasing_options?.totals?.rate?.net;

              const vehicleData = app.offer?.vehicle_data;
              const carName =
                vehicleData?.brand && vehicleData?.name
                  ? `${vehicleData.brand} ${vehicleData.name}`
                  : vehicleData?.full_name;
              const variantName =
                vehicleData?.brand && vehicleData?.name
                  ? vehicleData?.full_name
                  : undefined;

              return (
                <div
                  key={app.uuid}
                  data-testid='car-selection-container'
                  className='min-w-0'
                >
                  <CarSelection
                    uuid={app.uuid}
                    img={vehicleData?.vehicleImageUrl}
                    appLeaseDate={lastChanged}
                    time={time}
                    carName={carName}
                    variantName={variantName}
                    type={app.type}
                    rate={rate}
                    listPrice={vehicleData?.emission_data?.totalPrice}
                    duration={app.offer?.leasing_options?.duration}
                    mileage={app.offer?.leasing_options?.mileage}
                    // sortedApps is newest-first, so index 0 is the latest application
                    isNewApplication={index === 0}
                  />
                </div>
              );
            })}
          </div>

          <hr className='mt-12 mb-0 h-px border-0 bg-(--vel-color-neutral-300,#dddede)' />
          <p className='font-text m-0! mt-4! text-[15px] leading-[1.6] text-(--vel-color-secondary-400,#0f3549)!'>
            {getText('car_selection_not_found')}{' '}
            <LinkInline
              component='button'
              emphasis='low'
              className='font-bold text-(--vel-color-secondary-400,#0f3549)!'
              onClick={openMobilityPortal}
              data-testid='add-new-car'
            >
              {getText('car_selection_add_new_car')}
            </LinkInline>
          </p>
        </main>
      </>
    );
  }

  return (
    <>
      <Header showSubHeader={false}></Header>
      <div className='header-main-content'>
        <Layout
          heading='Willkommen bei Ayvens'
          subHeading='Ihr kompetenter Partner für Geschäfts- und Privatleasing'
        >
          <div className='overview-container mobile-hidden'>
            <img
              src={CouplePhoto}
              alt='photo_of_couples'
              className='couple-photo'
            />
            <div className='text-box'>
              <h2 className='heading'>
                Noch ohne passendes Fahrzeug unterwegs?
              </h2>
              <p className='content-normal'>
                Dann wird es Zeit, deinen Traumwagen zu finden! Begib dich auf
                die spannende Reise durch unsere vielfältige Auswahl an
                Fahrzeugen auf mobility.ayvens.de. Hier warten unterschiedliche
                Modelle und Hersteller darauf, von dir entdeckt zu werden. Dein
                perfektes Fahrerlebnis beginnt mit der richtigen Wahl. Wir
                stehen bereit, um dich auf diesem Weg zu unterstützen. Los
                geht's!
              </p>
              <Button
                variant='secondary-filled'
                size='m'
                onClick={openMobilityPortal}
              >
                weiter zu mobility.ayvens.de
              </Button>
            </div>
          </div>
          <div className='overview-container mobile-only'>
            <ButtonGroup
              type='default'
              style={{ padding: 0 }}
              buttonOneProps={{
                type: 'button',
                children: 'Privatkunden',
                onClick: openMobilityPortal,
              }}
            />
            <ButtonGroup
              type='default'
              style={{ padding: 0 }}
              buttonOneProps={{
                type: 'button',
                children: 'Geschäftskunden',
                onClick: openMobilityPortal,
              }}
            />
          </div>
        </Layout>
      </div>
    </>
  );
};

export default ActiveAppsOverview;
